import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Share2, Check, Loader2, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';
import { referralsAPI } from '@/lib/api';

export function InviteModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchReferralLink();
    }
  }, [open]);

  const fetchReferralLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await referralsAPI.getMyLink();
      setLinkData(response.data);
    } catch (err) {
      console.error('Failed to fetch referral link:', err);
      setError('Não foi possível carregar seu link de indicação.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!linkData?.referral_link) return;
    
    try {
      await navigator.clipboard.writeText(linkData.referral_link);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Não foi possível copiar o link');
    }
  };

  const handleShare = async () => {
    if (!linkData?.referral_link) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Junte-se à U-Clean!',
          text: 'Estou na U-Clean, uma rede de apoio à recuperação. Venha fazer parte!',
          url: linkData.referral_link,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Convidar Amigos
          </DialogTitle>
          <DialogDescription>
            Convide amigos para a U-Clean e ganhe tokens AWA por cada pessoa que se cadastrar!
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={fetchReferralLink} variant="outline">
                Tentar novamente
              </Button>
            </div>
          ) : (
            <>
              {/* Recompensa */}
              <div className="bg-amber-50 rounded-lg p-4 mb-4 flex items-center gap-3">
                <Gift className="h-8 w-8 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-900">Ganhe tokens!</p>
                  <p className="text-sm text-amber-700">
                    Receba <strong>1.000 tokens AWA</strong> por cada amigo que se cadastrar.
                  </p>
                </div>
              </div>

              {/* Link */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="referral-link">Seu link de indicação:</Label>
                <div className="flex gap-2">
                  <Input
                    id="referral-link"
                    value={linkData?.referral_link || ''}
                    readOnly
                    className="flex-1 text-sm"
                  />
                  <Button
                    onClick={handleCopy}
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Código */}
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">Seu código:</p>
                <p className="text-2xl font-bold tracking-wider text-purple-600">
                  {linkData?.referral_code || '---'}
                </p>
              </div>

              {/* Estatísticas */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total de convites usados:</span>
                  <span className="font-semibold">{linkData?.total_uses || 0}</span>
                </div>
              </div>

              {/* Botão Compartilhar */}
              <Button onClick={handleShare} className="w-full bg-purple-600 hover:bg-purple-700">
                <Share2 className="h-4 w-4 mr-2" />
                Compartilhar Link
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
